import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Space,
  Spin,
  Typography,
  Modal,
  Tooltip,
  Table,
  TableColumnsType,
  Tag,
  TablePaginationConfig,
  Button,
  Tabs,
  Statistic,
  Empty,
  Input,
  message,
} from "antd";
import {
  SettingOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  FileTextOutlined,
  EyeOutlined,
  PlusOutlined,
  CopyOutlined,
  CalendarOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { ApplicationService } from "../../services/ApplicationService";
import moment from "moment";
import ReconnectingWebSocket from "reconnecting-websocket";
import { usePermissions } from "../../PermissionsProvider";
import { Session, Turn } from "../../model_gen/session";
import { Application, Config } from "../../model_gen/application";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { SessionService } from "../../services/SessionService";
import { calculateScore, getSessionScoreColor } from "../Session/SessionUtils";
import { OutlineButton } from "../shared/button/Buttons";
import SessionOverview from "../Session/SessionOverview";
import { EvaluateResult } from "../../model_gen/chat";
import IntentsTable from "../shared/IntentsTable";
import ModelTable from "../shared/ModelTable";

const { TabPane } = Tabs;

const ApplicationOverview: React.FC = () => {
  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    tabIcon: {
      marginRight: "8px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    aiIconStyle: {
      width: "12px",
      height: "12px",
      color: "#8c8c8c",
      marginLeft: "4px",
      verticalAlign: "text-top",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    actionButton: (hovered: boolean) => ({
      borderRadius: "6px",
      color: "#1890ff",
      display: "flex",
      alignItems: "center",
      padding: "0 10px",
      height: "32px",
      transition: "all 0.3s",
      background: hovered ? "#f0f7ff" : "transparent",
      boxShadow: hovered ? "0 2px 4px rgba(24, 144, 255, 0.1)" : "none",
    }),
  };
  const { apiKey } = usePermissions();
  const { applicationId } = useParams<{ applicationId: string }>();
  const [application, setApplication] = useState<Partial<Application> | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const pageSize = 10;
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const lastMessageTimeRef = useRef<Date>(new Date());
  const [originalConfig, setOriginalConfig] = useState<Config>();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sessionsByPage, setSessionsByPage] = useState<
    Record<number, Session[]>
  >({});
  const [activeTab, setActiveTab] = useState<string>("overview");

  const updateConfig = (
    config: Partial<Config>,
    loadingHandler: (loading: boolean) => void | null,
    save: boolean = true
  ) => {
    let newConfig: Partial<Config> = application?.meta;
    if (!newConfig) {
      newConfig = config;
    }
    if (!application) {
      return;
    }
    const newApplication = {
      ...application,
      meta: {
        ...newConfig,
        ...config,
      },
    };
    setApplication(newApplication);
    if (!save) {
      return;
    }
    ApplicationService.setApplicationConfig(
      application.id,
      config,
      loadingHandler,
      apiKey
    ).subscribe(
      (updatedConfig: Config) => {
        setApplication((prevState) => {
          if (!prevState) return prevState;
          return {
            ...prevState,
            meta: updatedConfig,
          };
        });
        message.success("Configuration updated successfully");
      },
      (error: any) => {
        console.error(error);
        message.error("Failed to update configuration");
      }
    );
  };

  const resetConfig = () => {
    if (originalConfig) {
      updateConfig(originalConfig, () => {});
      message.success("Configuration reset successfully");
    }
  };

  // Update total sessions from application metadata
  const updateTotalSessionsFromMetadata = useCallback(() => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      setTotalSessions(Number(application.meta.metadata.session_count) || 0);
    }
  }, [application]);

  useEffect(() => {
    if (applicationId) {
      ApplicationService.getApplication(
        Number(applicationId),
        setIsLoading,
        apiKey
      ).subscribe(
        (app: Application) => {
          setApplication(app);
          setOriginalConfig(app.meta);

          // Set total sessions from metadata
          if (app.meta?.metadata?.session_count !== undefined) {
            setTotalSessions(Number(app.meta.metadata.session_count) || 0);
          }
        },
        (error: any) => {
          console.error(error);
          message.error("Failed to load application");
        }
      );
    }
    return openWebsocket();
  }, [applicationId]);

  // Update total sessions when application metadata changes
  useEffect(() => {
    updateTotalSessionsFromMetadata();
  }, [updateTotalSessionsFromMetadata]);

  const loadSessions = useCallback(() => {
    if (!applicationId || activeTab !== "sessions") return;

    if (!sessionsByPage[currentPage]) {
      setSessionsLoading(true);
      SessionService.getApplicationSessions(
        applicationId,
        (currentPage - 1) * pageSize,
        pageSize,
        setSessionsLoading,
        apiKey
      ).subscribe(
        ({ sessions, total }: { sessions: Session[]; total: number }) => {
          const processedSessions = sessions.map((session) => ({
            ...session,
            timeline: session.timeline || [],
            cost: session.cost ?? -1,
          }));

          setSessions(processedSessions);
          // Update total sessions from API response as a fallback
          if (
            total &&
            (!application?.meta?.metadata?.session_count ||
              total > Number(application.meta.metadata.session_count))
          ) {
            setTotalSessions(total);
          }
          setSessionsByPage((prev) => ({
            ...prev,
            [currentPage]: processedSessions,
          }));
        },
        (error: any) => {
          console.error(error);
          message.error("Failed to load sessions");
        }
      );
    } else {
      setSessions(sessionsByPage[currentPage]);
    }
  }, [
    applicationId,
    currentPage,
    pageSize,
    apiKey,
    sessionsByPage,
    activeTab,
    application,
  ]);

  useEffect(() => {
    if (applicationId) {
      loadSessions();
    }
  }, [loadSessions, refreshTrigger, activeTab]);

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .custom-tabs .ant-tabs-tab {
        transition: all 0.3s ease;
        border-radius: 6px 6px 0 0 !important;
        padding: 8px 16px !important;
      }
      .custom-tabs .ant-tabs-tab:hover {
        color: #1890ff;
      }
      .custom-tabs .ant-tabs-tab-active {
        background-color: #f0f7ff !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-modal-content {
        border-radius: 12px;
        overflow: hidden;
      }
      .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .ant-statistic-title {
        margin-bottom: 4px !important;
        position: absolute;
        top: 14px;
      }
      .ant-statistic-content {
        margin-top: 22px; /* Add space for the title above */
        display: flex;
        align-items: center;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .application-overview-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .application-overview-container .ant-btn {
        transition: all 0.2s ease;
      }
      /* Fix statistic icon alignment */
      .ant-statistic-content {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-prefix {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-value {
        display: inline-flex;
        align-items: center;
        word-break: break-word;
        white-space: normal;
      }
      .stats-card {
        position: relative;
        padding-top: 10px;
        height: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const openWebsocket = () => {
    closeWsConnection();

    const ws: any = new ReconnectingWebSocket(
      `wss://09hidyy627.execute-api.us-west-2.amazonaws.com/production?key=${applicationId}&type=NEW_SESSION,SESSION_UPDATE`
    );
    wsRef.current = ws;

    ws.addEventListener("message", (message: any) => {
      lastMessageTimeRef.current = new Date();
      const data = JSON.parse(message.data);
      switch (data.event_type) {
        case "NEW_SESSION":
          handleNewSession(data);
          break;
        case "SESSION_UPDATE":
          handleNewSessionTurn(data);
          break;
      }
    });

    // create an interval that checks the last message time and refreshes connection if needed
    const checkInterval = setInterval(() => {
      const now = new Date();
      // Check the payments WS
      const diffInMinutesWS =
        (now.getTime() - lastMessageTimeRef.current.getTime()) / (1000 * 60);

      if (diffInMinutesWS >= 1) {
        // adjust time as needed
        if (wsRef.current) {
          wsRef.current.reconnect(1000, "Manual reconnect");
          lastMessageTimeRef.current = new Date();
        }
      }
    }, 1000 * 5); // check every 5 seconds

    console.log("Opened WS connection");

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      closeWsConnection();
    };
  };

  const closeWsConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      console.log("Closed WS connection");
    }
  };

  const handleNewSession = (data: any) => {
    const newSession: Session = JSON.parse(data.event_data);
    if (newSession.timeline == undefined) {
      newSession.timeline = [];
    }
    if (!newSession.cost) {
      newSession.cost = -1;
    }
    const sessionId = newSession.session_id;

    // Set the date_created to the current time in seconds
    newSession.date_created = Date.now();

    setSessions((prevSessions) => {
      const updatedSessions = [...prevSessions];
      const sessionExists = updatedSessions?.some(
        (session) => session.session_id === sessionId
      );

      if (!sessionExists && currentPage == 1) {
        updatedSessions.unshift(newSession); // Prepend the new session to the beginning of the sessions array
      }

      return updatedSessions;
    });

    // Update total session count
    setTotalSessions((prev) => prev + 1);
  };

  const handleNewSessionTurn = (data: any) => {
    const turn: Turn = JSON.parse(data.event_data);
    const sessionId = turn.request?.session_id || turn.eval_request?.session_id;

    if (sessionId) {
      setSessions((prevSessions) => {
        const updatedSessions = [...prevSessions];
        const sessionIndex = updatedSessions.findIndex(
          (session) => session.session_id === sessionId
        );

        if (sessionIndex !== -1) {
          const session = updatedSessions[sessionIndex];
          const turnIndex = session.timeline.findIndex(
            (t) =>
              (t.request?.request_id &&
                turn.request?.request_id &&
                t.request.request_id === turn.request.request_id) ||
              (t.response?.request_id &&
                turn.response?.request_id &&
                t.response.request_id === turn.response.request_id) ||
              (t.eval_request?.request_id &&
                turn.eval_request?.request_id &&
                t.eval_request.request_id === turn.eval_request.request_id)
          );
          if (turnIndex !== -1) {
            const existingTurn = session.timeline[turnIndex];
            session.timeline[turnIndex] = {
              ...existingTurn,
            };
            if (turn.request?.request_id) {
              session.timeline[turnIndex].request = turn.request;
            }
            if (turn.response?.request_id) {
              session.timeline[turnIndex].response = turn.response;
            }
            if (turn.eval_request?.request_id) {
              session.timeline[turnIndex].eval_request = turn.eval_request;
            }
          } else if (turn.request || turn.response) {
            session.timeline.push(turn);
          }

          updatedSessions[sessionIndex] = session;
        }

        return updatedSessions;
      });
    }
  };
  const sessionColumns: TableColumnsType<Session> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Session ID",
      key: "session_id",
      width: "20%",
      render: (_, session: Session) => {
        const sessionId = session.session_id || "";
        const shortId =
          sessionId.length > 12
            ? `${sessionId.substring(0, 6)}...${sessionId.substring(
                sessionId.length - 6
              )}`
            : sessionId;

        return (
          <Space>
            <Typography.Text style={styles.monospaceFont} copyable={false}>
              {shortId}
            </Typography.Text>
            <Button
              icon={<CopyOutlined />}
              size="small"
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(sessionId);
                message.success({
                  content: "Session ID copied to clipboard",
                  icon: <CopyOutlined style={{ color: "#52c41a" }} />,
                });
              }}
            />
          </Space>
        );
      },
    },
    {
      title: "Last Activity",
      key: "last_activity",
      width: "15%",
      render: (_, session: Session) => {
        const lastRequestTimeMoment = session.date_ended
          ? moment(session.date_ended)
          : moment(session.date_created);

        return (
          <Tooltip
            title={lastRequestTimeMoment.format("MMMM D, YYYY h:mm:ss A")}>
            {lastRequestTimeMoment.fromNow()}
          </Tooltip>
        );
      },
    },
    {
      title: "Score",
      key: "score",
      width: "10%",
      render: (_, session: Session) => (
        <Tag color={getSessionScoreColor(session)} style={styles.tagStyle}>
          {calculateScore(session)}
        </Tag>
      ),
    },
    {
      title: "Requests",
      key: "requests",
      width: "10%",
      render: (_, session: Session) => (
        <Typography.Text
          strong
          style={{
            color: session.timeline.length > 0 ? "#1890ff" : "#8c8c8c",
          }}>
          {session.timeline.length}
        </Typography.Text>
      ),
    },
    {
      title: "Faults",
      key: "faults",
      width: "10%",
      render: (_, session: Session) => {
        const faultsCount = session.timeline.reduce((acc, turn) => {
          const hasFault =
            turn.eval_request?.eval_results_set?.evaluation_results?.some(
              (evalResult: EvaluateResult) => evalResult.status === "FAULT"
            );
          return acc + (hasFault ? 1 : 0);
        }, 0);
        return (
          <Typography.Text
            strong
            style={{ color: faultsCount > 0 ? "#f5222d" : "#8c8c8c" }}>
            {faultsCount}
          </Typography.Text>
        );
      },
    },
    {
      title: "Cost",
      key: "cost",
      width: "10%",
      render: (_, session: Session) => {
        // Format the cost to 3 decimal points like '$0.000'
        if (session.cost < 0) {
          return (
            <Typography.Text type="secondary">Calculating...</Typography.Text>
          );
        }
        return (
          <Typography.Text strong style={{ color: "#52c41a" }}>
            ${session.cost.toFixed(3)}
          </Typography.Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "center",
      render: (_, session: Session) => {
        return (
          <Button
            type="text"
            className="details-button"
            style={{
              borderRadius: "6px",
              color: "#1890ff",
              display: "flex",
              alignItems: "center",
              padding: "0 10px",
              height: "32px",
              transition: "all 0.2s",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleSessionClick(session);
            }}
            icon={<EyeOutlined style={{ marginRight: "4px" }} />}>
            Details
          </Button>
        );
      },
    },
  ];

  const navigate = useNavigate();

  const handleSessionClick = (session: Session) => {
    setSelectedSessionId(session.session_id);
    setSessionModalVisible(true);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleSessionModalClose = () => {
    setSelectedSessionId(null);
    setSessionModalVisible(false);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
  };

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  // Get the session count from application metadata or fallback to state
  const getSessionCount = () => {
    if (application?.meta?.metadata?.session_count !== undefined) {
      return Number(application.meta.metadata.session_count) || 0;
    }
    return totalSessions;
  };

  return (
    <div
      className="application-overview-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/applications">Applications</Link> /{" "}
                {application?.application_name}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {application?.application_name}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage intents, monitor sessions, and configure application
                settings
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}>
        {/* Overview Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>
            <Row gutter={[24, 24]}>
              {/* Stats Row */}
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <div
                      style={styles.statsCard("#1890ff")}
                      className="stats-card">
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Total Sessions
                          </Typography.Text>
                        }
                        value={getSessionCount()}
                        prefix={
                          <FileTextOutlined
                            style={{
                              color: "#1890ff",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div
                      style={styles.statsCard("#52c41a")}
                      className="stats-card">
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Application Reference
                          </Typography.Text>
                        }
                        value={application?.application_ref_name || "-"}
                        prefix={
                          <InfoCircleOutlined
                            style={{
                              color: "#52c41a",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                        formatter={(value) => (
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}>
                            {value}
                          </span>
                        )}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div
                      style={styles.statsCard("#722ed1")}
                      className="stats-card">
                      <Statistic
                        title={
                          <Typography.Text
                            style={{ fontSize: "12px", color: "#8c8c8c" }}>
                            Date Created
                          </Typography.Text>
                        }
                        value={moment(application?.date_created).format(
                          "MMM D, YYYY"
                        )}
                        prefix={
                          <CalendarOutlined
                            style={{
                              color: "#722ed1",
                              marginRight: "8px",
                              fontSize: "16px",
                            }}
                          />
                        }
                        valueStyle={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#262626",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* Intents Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <FileTextOutlined style={styles.tabIcon} />
              Intents
            </span>
          }
          key="intents">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Intents
              </Typography.Title>
              <Link to={`/application/${applicationId}/intent/new`}>
                <Button type="primary" icon={<PlusOutlined />}>
                  New Intent
                </Button>
              </Link>
            </div>
            <IntentsTable
              applicationId={application?.id}
              showApplicationColumn={false}
              styles={styles}
              emptyText={
                <Empty
                  description={
                    <div>
                      <Typography.Text
                        strong
                        style={{ display: "block", marginBottom: "8px" }}>
                        No intents found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create your first intent to get started.
                      </Typography.Text>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              }
            />
          </div>
        </TabPane>

        {/* Sessions Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <HistoryOutlined style={styles.tabIcon} />
              Sessions
            </span>
          }
          key="sessions">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Sessions
              </Typography.Title>
              <Input.Search
                placeholder="Search sessions"
                style={{ width: 300 }}
                allowClear
              />
            </div>
            <div style={styles.tableContainer}>
              <Table
                rowKey="session_id"
                columns={sessionColumns}
                dataSource={sessions}
                loading={sessionsLoading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: getSessionCount(),
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    // Handle page size change
                    if (pageSize && pageSize !== pageSize) {
                      setSessionsByPage({});
                    }
                  },
                  onShowSizeChange: (current, size) => {
                    setCurrentPage(1);
                    setSessionsByPage({});
                  },
                }}
                onChange={handleTableChange}
                onRow={(record) => ({
                  onClick: () => handleSessionClick(record),
                  style: styles.hoverRow,
                })}
                rowClassName={() => "interactive-row"}
                locale={{
                  emptyText: (
                    <Empty
                      description={
                        <div>
                          <Typography.Text
                            strong
                            style={{ display: "block", marginBottom: "8px" }}>
                            No sessions found
                          </Typography.Text>
                          <Typography.Text type="secondary">
                            No sessions have been created for this application
                            yet.
                          </Typography.Text>
                        </div>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </div>
          </div>
        </TabPane>

        {/* Models Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <ApiOutlined style={styles.tabIcon} />
              Models
            </span>
          }
          key="models">
          <div style={styles.contentCard}>
            <ModelTable apiKey={apiKey} applicationId={Number(applicationId)} />
          </div>
        </TabPane>

        {/* Configuration Tab */}
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SettingOutlined style={styles.tabIcon} />
              Configuration
            </span>
          }
          key="configuration">
          <div style={styles.contentCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "16px",
              }}>
              <OutlineButton icon={<ReloadOutlined />} onClick={resetConfig}>
                Reset to Default
              </OutlineButton>
            </div>
            <ConfigurationPanel
              config={application?.meta}
              updateConfig={updateConfig}
              resetConfig={resetConfig}
              showSystemPrompt={false}
            />
          </div>
        </TabPane>
      </Tabs>

      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 600,
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 24px",
              margin: "-20px -24px 20px",
            }}>
            <HistoryOutlined />
            <span>Session Details</span>
          </div>
        }
        open={sessionModalVisible}
        onCancel={handleSessionModalClose}
        footer={null}
        width={1000}
        keyboard={false}
        bodyStyle={{ padding: "0 24px 24px" }}
        destroyOnClose={true}
        style={{ top: 20 }}>
        {selectedSessionId && applicationId && (
          <SessionOverview
            sessionId={selectedSessionId}
            applicationId={applicationId}
            refreshTrigger={refreshTrigger}
          />
        )}
      </Modal>
    </div>
  );
};

export default ApplicationOverview;
